import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import type { ActivityType } from '@/types'

interface Props {
  activityTypes: ActivityType[]
  selectedIds: number[] // empty = all selected (no filter)
  onChange: (ids: number[]) => void
}

export function ActivityTypeFilter({ activityTypes, selectedIds, onChange }: Props) {
  const { t } = useTranslation()
  const allSelected = selectedIds.length === 0

  const toggle = (id: number) => {
    if (allSelected) {
      // First click when all selected: select only this one (exclude others)
      onChange([id])
    } else if (selectedIds.includes(id)) {
      // Deselect: remove from list
      const newIds = selectedIds.filter((i) => i !== id)
      // If would result in all types being selected, reset to empty (no filter)
      onChange(newIds.length === 0 ? [] : newIds)
    } else {
      // Select: add to list
      const newIds = [...selectedIds, id]
      // If all types are now selected, reset to empty (no filter)
      onChange(newIds.length === activityTypes.length ? [] : newIds)
    }
  }

  const clearFilter = () => onChange([])

  const isSelected = (id: number) => allSelected || selectedIds.includes(id)

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium text-muted-foreground">{t('filter.label')}:</span>
      {activityTypes.map((type) => (
        <Button
          key={type.id}
          variant={isSelected(type.id) ? 'default' : 'outline'}
          size="sm"
          onClick={() => toggle(type.id)}
          style={
            isSelected(type.id)
              ? { backgroundColor: type.color, borderColor: type.color }
              : { borderColor: type.color, color: type.color }
          }
        >
          {type.name}
        </Button>
      ))}
      {!allSelected && (
        <Button variant="ghost" size="sm" onClick={clearFilter} className="text-muted-foreground">
          {t('filter.clearAll')}
        </Button>
      )}
    </div>
  )
}
